/**
 * SOLOMON — SIGNALS PANEL
 * Read-only dashboard over interaction_signals: totals, per-model stats,
 * per-domain counts, recent activity. Fetched once on mount — user closes
 * and reopens the modal to refresh.
 *
 * Lives inside the Tools-menu ToolModal chrome. No writes, no real-time.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './signalsPanel.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function SignalsPanel() {
  const { authFetch } = useAuth();
  const [state, setState] = useState({ loading: true, refreshing: false, error: null, data: null });

  const load = async (isRefresh = false) => {
    setState(prev => isRefresh
      ? { ...prev, refreshing: true, error: null }
      : { loading: true, refreshing: false, error: null, data: null }
    );
    try {
      const res = await authFetch(`${API_BASE}/api/signals/summary`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      if (!body.success) throw new Error(body.error || 'Failed to load');
      setState({ loading: false, refreshing: false, error: null, data: body.data });
    } catch (err) {
      setState(prev => ({
        loading: false,
        refreshing: false,
        error: err.message || 'Failed to load',
        data: isRefresh ? prev.data : null,
      }));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.loading) {
    return (
      <div className="signals-panel">
        <div className="signals-breathing">Loading signals…</div>
      </div>
    );
  }

  if (state.error && !state.data) {
    return (
      <div className="signals-panel">
        <div className="signals-error">
          <p>Couldn't load signals.</p>
          <button className="signals-retry" onClick={() => load(false)}>Retry</button>
        </div>
      </div>
    );
  }

  const { totals, by_model, by_domain, recent } = state.data;
  const moreCount = Math.max(0, (totals.signals || 0) - recent.length);
  return (
    <div className="signals-panel">
      {/* Refresh control — header-aligned row above totals */}
      <div className="signals-toolbar">
        <button
          className="signals-refresh"
          onClick={() => load(true)}
          disabled={state.refreshing}
          title="Refresh signals"
          aria-label="Refresh signals"
        >
          <RefreshIcon spinning={state.refreshing} />
          <span>{state.refreshing ? 'Refreshing…' : 'Refresh'}</span>
        </button>
        {state.error && state.data && (
          <span className="signals-stale">Refresh failed — showing prior data.</span>
        )}
      </div>

      {/* Totals — three stat cards */}
      <div className="signals-totals">
        <StatCard label="Signals" value={totals.signals} />
        <StatCard label="Interactions" value={totals.interactions} />
        <StatCard label="Accepted" value={totals.accepted} />
      </div>

      {/* Models */}
      <Section title="Models">
        {by_model.length === 0 ? (
          <p className="signals-empty">No model interactions yet.</p>
        ) : (
          <table className="signals-table">
            <thead>
              <tr><th>Model</th><th className="num">Uses</th><th className="num">Avg conf.</th><th className="num">Avg time</th></tr>
            </thead>
            <tbody>
              {by_model.map(m => (
                <tr key={m.model}>
                  <td>{m.model}</td>
                  <td className="num">{m.count}</td>
                  <td className="num">{m.avg_confidence != null ? `${m.avg_confidence}%` : '—'}</td>
                  <td className="num">{m.avg_response_ms != null ? formatMs(m.avg_response_ms) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Domains */}
      <Section title="Domains">
        {by_domain.length === 0 ? (
          <p className="signals-empty">No domains classified yet.</p>
        ) : (
          <table className="signals-table">
            <thead>
              <tr><th>Domain</th><th className="num">Count</th></tr>
            </thead>
            <tbody>
              {by_domain.map(d => (
                <tr key={d.domain}>
                  <td>{d.domain}</td>
                  <td className="num">{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Recent activity */}
      <Section title={`Recent activity${recent.length > 0 ? ` (${recent.length})` : ''}`}>
        {recent.length === 0 ? (
          <p className="signals-empty">No recent interactions.</p>
        ) : (
          <>
            <div className="signals-recent-list">
              {recent.map((r, i) => (
                <div key={i} className="signals-recent-row">
                  <span className="signals-recent-time">{formatTimestamp(r.timestamp)}</span>
                  <span className="signals-recent-model">{r.model || '—'}</span>
                  <span className="signals-recent-conf">{r.confidence != null ? `${r.confidence}%` : '—'}</span>
                  <span className="signals-recent-dur">{r.response_time_ms != null ? formatMs(r.response_time_ms) : '—'}</span>
                </div>
              ))}
            </div>
            {moreCount > 0 && (
              <p className="signals-recent-more">
                … and {moreCount} more earlier signal{moreCount === 1 ? '' : 's'}
              </p>
            )}
          </>
        )}
      </Section>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────

function StatCard({ label, value }) {
  return (
    <div className="signals-stat-card">
      <div className="signals-stat-value">{value}</div>
      <div className="signals-stat-label">{label}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="signals-section">
      <div className="signals-section-header">{title}</div>
      {children}
    </div>
  );
}

function formatMs(ms) {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// Lucide `RefreshCw` — clockwise rotation affordance
function RefreshIcon({ spinning }) {
  return (
    <svg
      className={spinning ? 'signals-refresh-icon-spin' : ''}
      width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}
